// src/pages/appointment/BathReminderPanel.js
// Panel para mostrar clientes que deben ser notificados para recordarles el próximo baño

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button, Form, Spinner, Table, Badge, InputGroup, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { getLastCompletedAppointmentsByClient, getAppointmentsWithFilters, createAppointment } from '../../services/appointmentApi';
import {
  getCurrentReminderByClient,
  createReminder,
  updateReminder,
  deleteReminder,
  markReminderAsCompleted,
  markReminderAsCancelled,
  getReminderHistoryByClient
} from '../../services/reminderApi';
import AppointmentForm from './AppointmentForm';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const BathReminderPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Estados
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [reminderAppointments, setReminderAppointments] = useState([]);
  const [currentReminders, setCurrentReminders] = useState({}); // { clientId: reminder }
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  
  // Filtros
  const [minDaysFilter, setMinDaysFilter] = useState(0);
  const [maxDaysFilter, setMaxDaysFilter] = useState(365);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estados para modales de recordatorios
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [appointmentSelectedDate, setAppointmentSelectedDate] = useState(null);
  const [reminderFormData, setReminderFormData] = useState({
    id_client: '',
    id_status: 1, // PENDIENTE
    reminder_date: '',
    notes: ''
  });
  const [completeFormData, setCompleteFormData] = useState({
    id_appointment: '',
    changed_by_person: 1
  });
  const [cancelFormData, setCancelFormData] = useState({
    notes: '',
    changed_by_person: 1
  });
  const [clientAppointments, setClientAppointments] = useState([]);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Calcular días transcurridos desde la última cita
  const calculateDaysSince = (dateString) => {
    if (!dateString) return null;
    
    const appointmentDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);
    
    const diffTime = today - appointmentDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  
  // Obtener color del badge según días transcurridos
  const getDaysBadgeColor = (days) => {
    if (days === null) return 'secondary';
    if (days < 30) return 'success';
    if (days < 60) return 'warning';
    return 'danger';
  };
  
  // Cargar últimas citas completadas
  const loadLastCompletedAppointments = useCallback(async () => {
    try {
      setLoading(true);
      
      const { ok, status, data } = await getLastCompletedAppointmentsByClient();
      
      if (ok && (status === 200 || data?.code === 200)) {
        const responseData = data.data || data.message || [];
        setAppointments(responseData);
      } else {
        const msg = data?.message || 'Error cargando últimas citas';
        showAlert(getErrorMessage({ data, status }) || msg, 'error');
      }
    } catch (error) {
      console.error('Error cargando últimas citas:', error);
      showAlert('Error inesperado al cargar últimas citas', 'error');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Cargar recordatorio actual de un cliente
  const loadCurrentReminder = useCallback(async (clientId) => {
    try {
      const { ok, status, data } = await getCurrentReminderByClient(clientId);
      
      if (ok && (status === 200 || data?.code === 200)) {
        const reminderData = data.data || data.message || null;
        setCurrentReminders(prev => ({
          ...prev,
          [clientId]: reminderData
        }));
        return reminderData;
      }
      return null;
    } catch (error) {
      console.error('Error cargando recordatorio actual:', error);
      return null;
    }
  }, []);

  // Cargar citas de un cliente para el selector de completar recordatorio
  const loadClientAppointments = useCallback(async (appointment) => {
    try {
      setModalLoading(true);
      // Obtener todas las citas futuras y filtrar por el cliente
      const today = new Date().toISOString().split('T')[0];
      const { ok, status, data } = await getAppointmentsWithFilters({
        from: today,
        limit: 200
      });
      
      if (ok && (status === 200 || data?.code === 200)) {
        const responseData = data.data || data.message || [];
        const allAppointments = responseData.appointments || responseData;
        // Filtrar citas del cliente (las citas tienen id_pet, y sabemos que el cliente tiene esta mascota)
        // O simplemente mostrar todas las citas futuras y el usuario selecciona
        setClientAppointments(allAppointments);
      }
    } catch (error) {
      console.error('Error cargando citas del cliente:', error);
      setClientAppointments([]);
    } finally {
      setModalLoading(false);
    }
  }, []);

  // Cargar historial de recordatorios
  const loadReminderHistory = useCallback(async (clientId) => {
    try {
      setModalLoading(true);
      const { ok, status, data } = await getReminderHistoryByClient(clientId);
      
      if (ok && (status === 200 || data?.code === 200)) {
        const responseData = data.data || data.message || [];
        setReminderHistory(responseData);
      }
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setModalLoading(false);
    }
  }, []);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadLastCompletedAppointments();
  }, [loadLastCompletedAppointments]);
  
  // Cargar recordatorios actuales cuando cambian las citas
  useEffect(() => {
    const loadReminders = async () => {
      const uniqueClientIds = [...new Set(appointments.map(apt => apt.id_client))];
      for (const clientId of uniqueClientIds) {
        await loadCurrentReminder(clientId);
      }
    };
    
    if (appointments.length > 0) {
      loadReminders();
    }
  }, [appointments, loadCurrentReminder]);
  
  // Filtrar clientes que deben ser recordados (18-40 días desde último baño)
  useEffect(() => {
    const reminders = appointments.filter(appointment => {
      const days = calculateDaysSince(appointment.start_at);
      // Clientes entre 18 y 40 días desde su último baño
      return days !== null && days >= 18 && days <= 40;
    });
    
    // Ordenar por días transcurridos (menor a mayor para mostrar los más cercanos a 20 días primero)
    reminders.sort((a, b) => {
      const daysA = calculateDaysSince(a.start_at);
      const daysB = calculateDaysSince(b.start_at);
      return (daysA || 0) - (daysB || 0);
    });
    
    setReminderAppointments(reminders);
  }, [appointments]);
  
  // Filtrar citas según los criterios
  useEffect(() => {
    let filtered = [...appointments];
    
    // Filtrar por días transcurridos
    filtered = filtered.filter(appointment => {
      const days = calculateDaysSince(appointment.start_at);
      if (days === null) return false;
      return days >= minDaysFilter && days <= maxDaysFilter;
    });
    
    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(appointment => {
        const clientName = (appointment.client_name || '').toLowerCase();
        const petName = (appointment.pet_name || '').toLowerCase();
        const phone = (appointment.phone || '').toLowerCase();
        const itemDescription = (appointment.item_description || '').toLowerCase();
        
        return clientName.includes(query) || 
               petName.includes(query) || 
               phone.includes(query) ||
               itemDescription.includes(query);
      });
    }
    
    // Ordenar por días transcurridos (mayor a menor)
    filtered.sort((a, b) => {
      const daysA = calculateDaysSince(a.start_at);
      const daysB = calculateDaysSince(b.start_at);
      return (daysB || 0) - (daysA || 0);
    });
    
    setFilteredAppointments(filtered);
  }, [appointments, minDaysFilter, maxDaysFilter, searchQuery]);
  
  // Formatear fecha para mostrar
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Formatear fecha y hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Abrir WhatsApp
  const handleOpenWhatsApp = (phone) => {
    if (!phone) return;
    
    // Limpiar el número de teléfono (remover espacios, guiones, etc.)
    const cleanPhone = phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}`;
    window.open(whatsappUrl, '_blank');
  };
  
  // Ir al perfil del cliente
  const handleViewCustomerProfile = (clientId) => {
    navigate(`/perfil-cliente/${clientId}`);
  };

  // Abrir modal de crear/editar recordatorio
  const handleOpenReminderModal = async (appointment) => {
    const clientId = appointment.id_client;
    setSelectedClient(appointment);
    
    // Cargar recordatorio actual si existe
    const currentReminder = await loadCurrentReminder(clientId);
    
    if (currentReminder) {
      // Editar recordatorio existente
      setSelectedReminder(currentReminder);
      // Convertir formato de BD (YYYY-MM-DD HH:mm:ss) a datetime-local (YYYY-MM-DDTHH:mm)
      let reminderDate = currentReminder.reminder_date || '';
      if (reminderDate && reminderDate.includes(' ')) {
        reminderDate = reminderDate.replace(' ', 'T').slice(0, 16);
      }
      setReminderFormData({
        id_client: clientId,
        id_status: currentReminder.id_status || 1,
        reminder_date: reminderDate,
        notes: currentReminder.notes || ''
      });
    } else {
      // Crear nuevo recordatorio
      setSelectedReminder(null);
      const reminderDate = new Date();
      reminderDate.setDate(reminderDate.getDate() + 1); // Mañana por defecto
      // Formato para datetime-local: YYYY-MM-DDTHH:mm
      const formattedDate = reminderDate.toISOString().slice(0, 16);
      setReminderFormData({
        id_client: clientId,
        id_status: 1, // PENDIENTE
        reminder_date: formattedDate,
        notes: ''
      });
    }
    
    setShowReminderModal(true);
  };

  // Cerrar modal de recordatorio
  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setSelectedClient(null);
    setSelectedReminder(null);
    setReminderFormData({
      id_client: '',
      id_status: 1,
      reminder_date: '',
      notes: ''
    });
  };

  // Guardar recordatorio (crear o actualizar)
  const handleSaveReminder = async () => {
    try {
      setModalLoading(true);
      
      // Convertir formato datetime-local a formato de base de datos
      const reminderData = {
        ...reminderFormData,
        reminder_date: reminderFormData.reminder_date.replace('T', ' ') + ':00'
      };
      
      let result;
      if (selectedReminder) {
        // Actualizar
        result = await updateReminder(selectedReminder.id_reminder, reminderData);
      } else {
        // Crear
        result = await createReminder(reminderData);
      }
      
      if (result.ok && (result.status === 200 || result.status === 201 || result.data?.code === 200 || result.data?.code === 201)) {
        showAlert(selectedReminder ? 'Recordatorio actualizado exitosamente' : 'Recordatorio creado exitosamente', 'success');
        handleCloseReminderModal();
        // Recargar recordatorio actual
        if (selectedClient) {
          await loadCurrentReminder(selectedClient.id_client);
        }
        // Recargar datos
        loadLastCompletedAppointments();
      } else {
        const msg = getErrorMessage(result) || 'Error al guardar recordatorio';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error guardando recordatorio:', error);
      showAlert('Error inesperado al guardar recordatorio', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Abrir modal de marcar como completado
  const handleOpenCompleteModal = async (appointment) => {
    setSelectedClient(appointment);
    const currentReminder = currentReminders[appointment.id_client];
    setSelectedReminder(currentReminder);
    
    if (currentReminder) {
      await loadClientAppointments(appointment);
      setCompleteFormData({
        id_appointment: '',
        changed_by_person: user?.id || 1
      });
      setShowCompleteModal(true);
    } else {
      showAlert('No hay recordatorio activo para este cliente', 'error');
    }
  };

  // Cerrar modal de completar
  const handleCloseCompleteModal = () => {
    setShowCompleteModal(false);
    setSelectedClient(null);
    setSelectedReminder(null);
    setCompleteFormData({
      id_appointment: '',
      changed_by_person: 1
    });
  };

  // Marcar recordatorio como completado
  const handleMarkAsCompleted = async () => {
    if (!selectedReminder || !completeFormData.id_appointment) {
      showAlert('Debe seleccionar una cita', 'error');
      return;
    }
    
    try {
      setModalLoading(true);
      
      const result = await markReminderAsCompleted(selectedReminder.id_reminder, completeFormData);
      
      if (result.ok && (result.status === 200 || result.data?.code === 200)) {
        showAlert('Recordatorio marcado como completado', 'success');
        handleCloseCompleteModal();
        if (selectedClient) {
          await loadCurrentReminder(selectedClient.id_client);
        }
        loadLastCompletedAppointments();
      } else {
        const msg = result.data?.message || 'Error al marcar como completado';
        showAlert(getErrorMessage(result) || msg, 'error');
      }
    } catch (error) {
      console.error('Error marcando como completado:', error);
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Abrir modal de marcar como cancelado
  const handleOpenCancelModal = (appointment) => {
    setSelectedClient(appointment);
    const currentReminder = currentReminders[appointment.id_client];
    setSelectedReminder(currentReminder);
    
    if (currentReminder) {
      setCancelFormData({
        notes: '',
        changed_by_person: user?.id || 1
      });
      setShowCancelModal(true);
    } else {
      showAlert('No hay recordatorio activo para este cliente', 'error');
    }
  };

  // Cerrar modal de cancelar
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedClient(null);
    setSelectedReminder(null);
    setCancelFormData({
      notes: '',
      changed_by_person: 1
    });
  };

  // Marcar recordatorio como cancelado
  const handleMarkAsCancelled = async () => {
    if (!selectedReminder) {
      showAlert('No hay recordatorio seleccionado', 'error');
      return;
    }
    
    try {
      setModalLoading(true);
      
      const result = await markReminderAsCancelled(selectedReminder.id_reminder, cancelFormData);
      
      if (result.ok && (result.status === 200 || result.data?.code === 200)) {
        showAlert('Recordatorio marcado como cancelado', 'success');
        handleCloseCancelModal();
        if (selectedClient) {
          await loadCurrentReminder(selectedClient.id_client);
        }
        loadLastCompletedAppointments();
      } else {
        const msg = result.data?.message || 'Error al marcar como cancelado';
        showAlert(getErrorMessage(result) || msg, 'error');
      }
    } catch (error) {
      console.error('Error marcando como cancelado:', error);
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Abrir modal de historial
  const handleOpenHistoryModal = async (appointment) => {
    setSelectedClient(appointment);
    await loadReminderHistory(appointment.id_client);
    setShowHistoryModal(true);
  };

  // Cerrar modal de historial
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedClient(null);
    setReminderHistory([]);
  };

  // Abrir modal de eliminar
  const handleOpenDeleteModal = (appointment) => {
    setSelectedClient(appointment);
    const currentReminder = currentReminders[appointment.id_client];
    setSelectedReminder(currentReminder);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminar
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedClient(null);
    setSelectedReminder(null);
  };

  // Eliminar recordatorio
  const handleDeleteReminder = async () => {
    if (!selectedReminder) {
      showAlert('No hay recordatorio seleccionado', 'error');
      return;
    }
    
    try {
      setModalLoading(true);
      
      const result = await deleteReminder(selectedReminder.id_reminder);
      
      if (result.ok && (result.status === 200 || result.data?.code === 200)) {
        showAlert('Recordatorio eliminado exitosamente', 'success');
        handleCloseDeleteModal();
        if (selectedClient) {
          await loadCurrentReminder(selectedClient.id_client);
        }
        loadLastCompletedAppointments();
      } else {
        const msg = result.data?.message || 'Error al eliminar recordatorio';
        showAlert(getErrorMessage(result) || msg, 'error');
      }
    } catch (error) {
      console.error('Error eliminando recordatorio:', error);
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Abrir modal de agendamiento de cita
  const handleOpenAppointmentModal = (appointment) => {
    setSelectedClient(appointment);
    // Establecer fecha por defecto (mañana)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setAppointmentSelectedDate(tomorrow.toISOString().split('T')[0]);
    setShowAppointmentModal(true);
  };

  // Cerrar modal de agendamiento
  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedClient(null);
    setAppointmentSelectedDate(null);
  };

  // Manejar creación de cita desde recordatorio
  const handleCreateAppointmentFromReminder = async (appointmentData) => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await createAppointment(appointmentData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        const newAppointmentId = data.data?.id_appointment || data.data?.id || null;
        
        // Si hay un recordatorio activo para este cliente, marcarlo como completado
        if (selectedClient && currentReminders[selectedClient.id_client] && newAppointmentId) {
          try {
            await markReminderAsCompleted(
              currentReminders[selectedClient.id_client].id_reminder,
              {
                id_appointment: newAppointmentId,
                changed_by_person: user?.id || 1
              }
            );
            showAlert('Cita agendada exitosamente y recordatorio marcado como completado', 'success');
          } catch (reminderError) {
            console.error('Error marcando recordatorio como completado:', reminderError);
            showAlert('Cita agendada exitosamente (pero hubo un error al actualizar el recordatorio)', 'success');
          }
        } else {
          showAlert(data?.message || 'Cita agendada exitosamente', 'success');
        }
        
        handleCloseAppointmentModal();
        // Recargar datos
        if (selectedClient) {
          await loadCurrentReminder(selectedClient.id_client);
        }
        loadLastCompletedAppointments();
      } else {
        const msg = data?.message || 'Error agendando cita';
        showAlert(getErrorMessage({ data, status }) || msg, 'error');
      }
    } catch (error) {
      console.error('Error agendando cita:', error);
      showAlert('Error inesperado al agendar cita', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Formatear fecha para mostrar en el modal
  const formatAppointmentDate = () => {
    if (!appointmentSelectedDate) return '';
    
    const date = new Date(appointmentSelectedDate);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    return date.toLocaleDateString('es-ES', options);
  };
  
  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            {/* Sección de Recordatorios Prioritarios */}
            {!loading && reminderAppointments.length > 0 && (
              <Card className="mb-4 border-warning">
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Clientes para Recordar Ahora
                    <Badge bg="dark" className="ms-2">{reminderAppointments.length}</Badge>
                  </h5>
                  <small className="text-muted">
                    Clientes con 18-40 días desde su último baño
                  </small>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Mascota</th>
                        <th>Último Servicio</th>
                        <th>Fecha Última Cita</th>
                        <th>Días Transcurridos</th>
                        <th>Teléfono</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reminderAppointments.map((appointment) => {
                        const daysSince = calculateDaysSince(appointment.start_at);
                        
                        return (
                          <tr key={`reminder-${appointment.id_client}-${appointment.id_pet}-${appointment.id_appointment}`}>
                            <td>
                              <strong>{appointment.client_name || 'N/A'}</strong>
                            </td>
                            <td>{appointment.pet_name || 'N/A'}</td>
                            <td>{appointment.item_description || 'N/A'}</td>
                            <td>
                              <small>{formatDateTime(appointment.start_at)}</small>
                            </td>
                            <td>
                              <Badge bg="warning" text="dark">
                                {daysSince !== null ? `${daysSince} días` : 'N/A'}
                              </Badge>
                            </td>
                            <td>
                              {appointment.phone ? (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-decoration-none"
                                  onClick={() => handleOpenWhatsApp(appointment.phone)}
                                >
                                  <i className="fab fa-whatsapp me-1 text-success"></i>
                                  {appointment.phone}
                                </Button>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleViewCustomerProfile(appointment.id_client)}
                                  title="Ver perfil del cliente"
                                >
                                  <i className="fas fa-user"></i>
                                </Button>
                                {appointment.phone && (
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleOpenWhatsApp(appointment.phone)}
                                    title="Enviar mensaje por WhatsApp"
                                  >
                                    <i className="fab fa-whatsapp"></i>
                                  </Button>
                                )}
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleOpenAppointmentModal(appointment)}
                                  title="Agendar cita"
                                >
                                  <i className="fas fa-calendar-plus"></i>
                                </Button>
                                <Button
                                  variant={currentReminders[appointment.id_client] ? "outline-warning" : "outline-secondary"}
                                  size="sm"
                                  onClick={() => handleOpenReminderModal(appointment)}
                                  title={currentReminders[appointment.id_client] ? "Editar recordatorio" : "Crear recordatorio"}
                                >
                                  <i className="fas fa-bell"></i>
                                </Button>
                                {currentReminders[appointment.id_client] && (
                                  <>
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleOpenCompleteModal(appointment)}
                                      title="Marcar como completado"
                                    >
                                      <i className="fas fa-check"></i>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleOpenCancelModal(appointment)}
                                      title="Marcar como cancelado"
                                    >
                                      <i className="fas fa-times"></i>
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => handleOpenHistoryModal(appointment)}
                                      title="Ver historial"
                                    >
                                      <i className="fas fa-history"></i>
                                    </Button>
                                    <Button
                                      variant="outline-dark"
                                      size="sm"
                                      onClick={() => handleOpenDeleteModal(appointment)}
                                      title="Eliminar recordatorio"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </>
                                )}
                              </div>
                              {currentReminders[appointment.id_client] && (
                                <small className="d-block mt-1 text-muted">
                                  <Badge bg="info" className="me-1">
                                    {currentReminders[appointment.id_client].status_name || 'ACTIVO'}
                                  </Badge>
                                </small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fas fa-bell me-2"></i>
                  Recordatorios de Baño
                </h4>
                <Button variant="outline-primary" onClick={loadLastCompletedAppointments}>
                  <i className="fas fa-sync-alt me-2"></i>
                  Actualizar
                </Button>
              </Card.Header>
              <Card.Body>

                {/* Filtros */}
                <Row className="mb-3">
                  <Col md={4}>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fas fa-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Buscar por cliente, mascota, teléfono..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Días mínimos desde última cita</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="365"
                      value={minDaysFilter}
                      onChange={(e) => setMinDaysFilter(parseInt(e.target.value) || 0)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Días máximos desde última cita</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="365"
                      value={maxDaysFilter}
                      onChange={(e) => setMaxDaysFilter(parseInt(e.target.value) || 365)}
                    />
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <small className="text-muted">
                      {filteredAppointments.length} resultado(s)
                    </small>
                  </Col>
                </Row>

                {/* Tabla de recordatorios */}
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    {filteredAppointments.length === 0 ? (
                      <Alert variant="info" className="text-center">
                        {searchQuery || minDaysFilter > 0 || maxDaysFilter < 365
                          ? 'No se encontraron clientes con los filtros aplicados'
                          : 'No hay clientes con citas completadas para mostrar'}
                      </Alert>
                    ) : (
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Cliente</th>
                            <th>Mascota</th>
                            <th>Último Servicio</th>
                            <th>Fecha Última Cita</th>
                            <th>Días Transcurridos</th>
                            <th>Teléfono</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAppointments.map((appointment) => {
                            const daysSince = calculateDaysSince(appointment.start_at);
                            
                            return (
                              <tr key={`${appointment.id_client}-${appointment.id_pet}-${appointment.id_appointment}`}>
                                <td>
                                  <strong>{appointment.client_name || 'N/A'}</strong>
                                </td>
                                <td>{appointment.pet_name || 'N/A'}</td>
                                <td>{appointment.item_description || 'N/A'}</td>
                                <td>
                                  <small>{formatDateTime(appointment.start_at)}</small>
                                </td>
                                <td>
                                  <Badge bg={getDaysBadgeColor(daysSince)}>
                                    {daysSince !== null ? `${daysSince} días` : 'N/A'}
                                  </Badge>
                                </td>
                                <td>
                                  {appointment.phone ? (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 text-decoration-none"
                                      onClick={() => handleOpenWhatsApp(appointment.phone)}
                                    >
                                      <i className="fab fa-whatsapp me-1 text-success"></i>
                                      {appointment.phone}
                                    </Button>
                                  ) : (
                                    'N/A'
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-1">
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => handleViewCustomerProfile(appointment.id_client)}
                                      title="Ver perfil del cliente"
                                    >
                                      <i className="fas fa-user"></i>
                                    </Button>
                                    {appointment.phone && (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => handleOpenWhatsApp(appointment.phone)}
                                        title="Enviar mensaje por WhatsApp"
                                      >
                                        <i className="fab fa-whatsapp"></i>
                                      </Button>
                                    )}
                                    <Button
                                      variant={currentReminders[appointment.id_client] ? "outline-warning" : "outline-secondary"}
                                      size="sm"
                                      onClick={() => handleOpenReminderModal(appointment)}
                                      title={currentReminders[appointment.id_client] ? "Editar recordatorio" : "Crear recordatorio"}
                                    >
                                      <i className="fas fa-bell"></i>
                                    </Button>
                                    {currentReminders[appointment.id_client] && (
                                      <>
                                        <Button
                                          variant="outline-success"
                                          size="sm"
                                          onClick={() => handleOpenCompleteModal(appointment)}
                                          title="Marcar como completado"
                                        >
                                          <i className="fas fa-check"></i>
                                        </Button>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => handleOpenCancelModal(appointment)}
                                          title="Marcar como cancelado"
                                        >
                                          <i className="fas fa-times"></i>
                                        </Button>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={() => handleOpenHistoryModal(appointment)}
                                          title="Ver historial"
                                        >
                                          <i className="fas fa-history"></i>
                                        </Button>
                                        <Button
                                          variant="outline-dark"
                                          size="sm"
                                          onClick={() => handleOpenDeleteModal(appointment)}
                                          title="Eliminar recordatorio"
                                        >
                                          <i className="fas fa-trash"></i>
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                  {currentReminders[appointment.id_client] && (
                                    <small className="d-block mt-1 text-muted">
                                      <Badge bg="info" className="me-1">
                                        {currentReminders[appointment.id_client].status_name || 'ACTIVO'}
                                      </Badge>
                                    </small>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de Crear/Editar Recordatorio */}
        <Modal show={showReminderModal} onHide={handleCloseReminderModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedReminder ? 'Editar Recordatorio' : 'Crear Recordatorio'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedClient?.client_name || ''}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fecha del Recordatorio *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={reminderFormData.reminder_date}
                  onChange={(e) => setReminderFormData(prev => ({
                    ...prev,
                    reminder_date: e.target.value
                  }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Notas</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={reminderFormData.notes}
                  onChange={(e) => setReminderFormData(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                  placeholder="Notas adicionales sobre el recordatorio..."
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseReminderModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveReminder} disabled={modalLoading}>
              {modalLoading ? <Spinner size="sm" className="me-2" /> : null}
              {selectedReminder ? 'Actualizar' : 'Crear'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Marcar como Completado */}
        <Modal show={showCompleteModal} onHide={handleCloseCompleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Marcar Recordatorio como Completado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedClient?.client_name || ''}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>ID de Cita Agendada *</Form.Label>
                <Form.Control
                  type="number"
                  value={completeFormData.id_appointment}
                  onChange={(e) => setCompleteFormData(prev => ({
                    ...prev,
                    id_appointment: e.target.value
                  }))}
                  placeholder="Ingrese el ID de la cita agendada"
                  required
                />
                <Form.Text className="text-muted">
                  Ingrese el ID de la cita que el cliente agendó. Puede encontrarlo en el calendario o en la gestión de citas.
                </Form.Text>
                {clientAppointments.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">Citas recientes disponibles:</small>
                    <Form.Select
                      size="sm"
                      className="mt-1"
                      onChange={(e) => {
                        if (e.target.value) {
                          setCompleteFormData(prev => ({
                            ...prev,
                            id_appointment: e.target.value
                          }));
                        }
                      }}
                    >
                      <option value="">Seleccionar de la lista...</option>
                      {clientAppointments.slice(0, 10).map((apt) => (
                        <option key={apt.id_appointment} value={apt.id_appointment}>
                          ID: {apt.id_appointment} - {formatDateTime(apt.start_at)} - {apt.pet_name || 'N/A'}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCompleteModal}>
              Cancelar
            </Button>
            <Button variant="success" onClick={handleMarkAsCompleted} disabled={modalLoading || !completeFormData.id_appointment}>
              {modalLoading ? <Spinner size="sm" className="me-2" /> : null}
              Marcar como Completado
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Marcar como Cancelado */}
        <Modal show={showCancelModal} onHide={handleCloseCancelModal}>
          <Modal.Header closeButton>
            <Modal.Title>Marcar Recordatorio como Cancelado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedClient?.client_name || ''}
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Notas de Cancelación</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={cancelFormData.notes}
                  onChange={(e) => setCancelFormData(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                  placeholder="Razón de la cancelación (opcional)..."
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCancelModal}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleMarkAsCancelled} disabled={modalLoading}>
              {modalLoading ? <Spinner size="sm" className="me-2" /> : null}
              Marcar como Cancelado
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Historial */}
        <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Historial de Recordatorios - {selectedClient?.client_name || ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalLoading ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" />
              </div>
            ) : reminderHistory.length === 0 ? (
              <Alert variant="info">No hay historial de recordatorios para este cliente.</Alert>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Notas</th>
                    <th>Cambiado por</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderHistory.map((history, index) => (
                    <tr key={index}>
                      <td>{formatDateTime(history.created_at || history.updated_at)}</td>
                      <td>
                        <Badge bg={
                          history.status_name === 'COMPLETADO' ? 'success' :
                          history.status_name === 'CANCELADO' ? 'danger' :
                          history.status_name === 'ENVIADO' ? 'info' : 'secondary'
                        }>
                          {history.status_name || 'N/A'}
                        </Badge>
                      </td>
                      <td>{history.notes || 'N/A'}</td>
                      <td>{history.changed_by_person || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseHistoryModal}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Eliminar Recordatorio */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar Recordatorio</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Está seguro de que desea eliminar el recordatorio para <strong>{selectedClient?.client_name || ''}</strong>?</p>
            <Alert variant="warning">
              Esta acción no se puede deshacer.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteModal}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteReminder} disabled={modalLoading}>
              {modalLoading ? <Spinner size="sm" className="me-2" /> : null}
              Eliminar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Agendamiento de Cita */}
        <Modal show={showAppointmentModal} onHide={handleCloseAppointmentModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Agendar Cita - {selectedClient?.client_name || ''}
              {appointmentSelectedDate && ` - ${formatAppointmentDate()}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AppointmentForm
              onSubmit={handleCreateAppointmentFromReminder}
              loading={modalLoading}
              selectedDate={appointmentSelectedDate}
              initialData={selectedClient ? {
                id_pet: selectedClient.id_pet || '',
                id_item: '',
                start_at: '',
                duration_min: 30,
                id_status: 12,
                create_by_person: user?.id || 1
              } : null}
              submitText="Agendar Cita"
              onAlert={showAlert}
            />
          </Modal.Body>
        </Modal>
        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default BathReminderPanel;
